import { isEmpty } from 'lodash'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { Link } from 'react-router-dom'

import Button from '@/components/Button'
import Card from '@/components/Card'
import DeleteConfirmationOverlay from '@/components/DeleteConfirmationOverlay'
import { Plus } from '@/components/Icons'
import PaginationTable from '@/components/PaginationTable'
import RecallConfirmationOverlay from '@/components/RecallConfirmationOverlay'
import { Role, useRoles } from '@/components/RolesContainer'

import columns from './columns'
import messages from './messages'

import './index.css'

import { connect } from 'react-redux'
import { usePrevious } from 'react-use'

import { getCurrentSpaceId } from '@/components/App/selectors'

function PublisherNotificationList(props: any) {
  const intl = useIntl()
  const roles = useRoles()

  const [state, _setState] = useState({
    selectedNotification: '',
    showDeleteConfirmation: false,
    showRecallConfirmation: false
  })
  const setState = (update: Partial<Parameters<typeof _setState>[0]>) => {
    return _setState(state => ({ ...state, ...(typeof update === 'function' ? update(state) : update) }))
  }

  // componentDidUpdate
  const prevProps = usePrevious(props)
  useEffect(() => {
    if (!prevProps) return

    if ((prevProps.isRecalling && !props.isRecalling) || (prevProps.isDeleting && !props.isDeleting)) {
      goToFirstPage(props.showDeleted)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevProps, props])

  const onPageSelect = (page: any, sortBy: any, sortOrder: any) => {
    props.loadPage(page, props.showDeleted, sortBy, sortOrder)
  }

  const goToFirstPage = (showDeleted: any) => {
    if (props.sortingColumn && !isEmpty(props.sortingColumn)) {
      props.loadPage(
        1,
        showDeleted,
        Object.keys(props.sortingColumn)[0],
        props.sortingColumn[Object.keys(props.sortingColumn)[0]].direction.toUpperCase()
      )
    } else {
      props.loadPage(1, showDeleted)
    }
  }

  const deleteNotification = () => {
    try {
      props.onDeleteNotification(state.selectedNotification)
    } finally {
      setState({
        showDeleteConfirmation: false,
        selectedNotification: ''
      })
    }
  }

  const recallNotification = () => {
    try {
      props.onRecallNotification(state.selectedNotification)
    } finally {
      setState({
        showRecallConfirmation: false,
        selectedNotification: ''
      })
    }
  }

  const showConfirmDelete = (notificationId: any) => {
    setState({ showDeleteConfirmation: true, selectedNotification: notificationId })
  }

  const showConfirmRecall = (notificationId: any) => {
    setState({ showRecallConfirmation: true, selectedNotification: notificationId })
  }

  const onCancelDelete = () => {
    setState({ showDeleteConfirmation: false, selectedNotification: '' })
  }

  const onCancelRecall = () => {
    setState({ showRecallConfirmation: false, selectedNotification: '' })
  }

  const toggleShowDeleted = () => {
    goToFirstPage(!props.showDeleted)
  }

  const {
    currentSpaceId,

    isDeleting,
    isRecalling,
    notifications,
    notificationsStart,
    notificationsTotal,
    onCloneNotification,
    onCreateNotification,
    onSelectNotification,
    onNavigateToViewLogs,
    perPage,
    showDeleted,
    sortingColumn
  } = props
  const { showDeleteConfirmation, showRecallConfirmation } = state
  const message = isRecalling ? { ...messages.recallingNotification } : { ...messages.deletingNotification }
  return (
    <Card isActionInProgress={isRecalling || isDeleting} actionMessage={<FormattedMessage {...message} />}>
      <Card.Header title={<FormattedMessage {...messages.recentNotifications} />}>
        {roles.hasSpaceRole(currentSpaceId, Role.SPACE_ADMIN_ROLE) && (
          <Link className="publisher-back-nav" to="notificationsources">
            <FormattedMessage {...messages.manageSources} />
          </Link>
        )}
        <Button color="primary" onClick={onCreateNotification}>
          <>
            {' '}
            <Plus /> <FormattedMessage {...messages.createNotification} />
          </>
        </Button>
      </Card.Header>

      <Card.Content>
        <div className="publisher-notifications">
          <PaginationTable
            columns={columns({
              intl,
              onSelectNotification,
              onCloneNotification: onCloneNotification,
              showConfirmDelete: showConfirmDelete,
              showConfirmRecall: showConfirmRecall,
              showDeleted: showDeleted,
              toggleShowDeleted: toggleShowDeleted,
              navigateToLogs: onNavigateToViewLogs
            })}
            onPaginateSelect={onPageSelect}
            pagination={{
              count: notificationsTotal,
              startIndex: notificationsStart,
              perPage: perPage
            }}
            rows={notifications}
            rowKey="id"
            rowItemType={<FormattedMessage {...messages.notifications} />}
            sortingColumn={sortingColumn}
          />
        </div>
      </Card.Content>

      {showDeleteConfirmation && (
        <DeleteConfirmationOverlay
          onConfirm={deleteNotification}
          onCancel={onCancelDelete}
          subMessage={<FormattedMessage {...messages.willBeRemoved} />}
        />
      )}

      {showRecallConfirmation && (
        <RecallConfirmationOverlay
          onConfirm={recallNotification}
          onCancel={onCancelRecall}
          subMessage={<FormattedMessage {...messages.willBeRecalled} />}
        />
      )}
    </Card>
  )
}

function mapStateToProps(state: any) {
  return {
    currentSpaceId: getCurrentSpaceId(state)
  }
}

PublisherNotificationList.displayName = 'PublisherNotificationList'

PublisherNotificationList.propTypes = {
  currentSpaceId: PropTypes.string.isRequired,

  isDeleting: PropTypes.bool,
  isRecalling: PropTypes.bool,
  loadPage: PropTypes.func.isRequired,
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      publisher: PropTypes.object,
      summary: PropTypes.object
    })
  ).isRequired,
  notificationsTotal: PropTypes.number.isRequired,
  notificationsStart: PropTypes.number.isRequired,
  onCreateNotification: PropTypes.func.isRequired,
  onDeleteNotification: PropTypes.func.isRequired,
  onRecallNotification: PropTypes.func.isRequired,
  onCloneNotification: PropTypes.func.isRequired,
  onSelectNotification: PropTypes.func.isRequired,
  onNavigateToViewLogs: PropTypes.func.isRequired,
  perPage: PropTypes.number.isRequired,
  showDeleted: PropTypes.bool.isRequired,
  sortingColumn: PropTypes.object
}

PublisherNotificationList.defaultProps = {
  isDeleting: false,
  isRecalling: false,
  notifications: [],
  onCreateNotification: () => {},
  onDeleteNotification: () => {},
  onRecallNotification: () => {},
  onCloneNotification: () => {},
  sortingColumn: null
}

export default connect(mapStateToProps)(PublisherNotificationList)
